import React, { useEffect, useMemo, useState } from "react";
import styles from "./BaseInput.module.css";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getDefaultValidationMessage(type, label) {
  const fieldName = label || "Este campo";

  switch (type) {
    case "email":
      return `${fieldName} no es un email válido`;
    case "password":
      return `${fieldName} no cumple con el formato requerido`;
    case "number":
      return `${fieldName} debe ser un número válido`;
    case "url":
      return `${fieldName} no es una URL válida`;
    case "tel":
      return `${fieldName} no es un teléfono válido`;
    default:
      return `${fieldName} no es válido`;
  }
}

function validateByType(type, value, options = {}) {
  const stringValue = String(value ?? "").trim();

  if (!stringValue) return null;

  switch (type) {
    case "email": {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(stringValue) ? null : "invalid";
    }

    case "password": {
      const minLength = options.minLength ?? 8;
      return stringValue.length >= minLength ? null : "invalid";
    }

    case "number": {
      return Number.isNaN(Number(stringValue)) ? "invalid" : null;
    }

    case "url": {
      try {
        new URL(stringValue);
        return null;
      } catch {
        return "invalid";
      }
    }

    case "tel": {
      const phoneRegex = /^[+]?[\d\s\-()]{6,20}$/;
      return phoneRegex.test(stringValue) ? null : "invalid";
    }

    default:
      return null;
  }
}

const BaseInput = React.forwardRef(function BaseInput(
  {
    label,
    error,
    leftIcon,
    rightIcon,
    className,
    inputClassName,
    onChange,

    type = "text",
    required = false,
    requiredMessage,
    validate,
    validateOnChange = true,
    onValidation,

    ...props
  },
  ref,
) {
  const [internalError, setInternalError] = useState("");

  const validationOptions = useMemo(
    () => ({
      minLength: props.minLength,
      maxLength: props.maxLength,
    }),
    [props.minLength, props.maxLength],
  );

  const controlledValue = props.value ?? "";

  const runValidation = (value) => {
    const stringValue = String(value ?? "").trim();

    if (required && !stringValue) {
      const message = requiredMessage || `${label || "Este campo"} es obligatorio`;
      setInternalError(message);
      onValidation?.(false, message);
      return message;
    }

    const defaultTypeValidation = validateByType(type, value, validationOptions);
    if (defaultTypeValidation) {
      const message = getDefaultValidationMessage(type, label);
      setInternalError(message);
      onValidation?.(false, message);
      return message;
    }

    if (typeof validate === "function") {
      const customMessage = validate(value);
      if (customMessage) {
        setInternalError(customMessage);
        onValidation?.(false, customMessage);
        return customMessage;
      }
    }

    setInternalError("");
    onValidation?.(true, "");
    return "";
  };

  function handleChange(e) {
    const value = typeof e === "string" ? e : (e?.target?.value ?? "");

    if (validateOnChange) {
      runValidation(value);
    }

    onChange?.(value, e);
  }

  useEffect(() => {
    if (validateOnChange) {
      runValidation(controlledValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledValue, type, required, requiredMessage, validate]);

  const displayedError = error || internalError;

  return (
    <div className={styles.wrapper}>
      {label ? <label className={styles.label}>{label}</label> : null}

      <div
        className={cx(
          styles.inputContainer,
          displayedError && styles.error,
          className,
        )}
      >
        {leftIcon ? <span className={styles.leftIcon}>{leftIcon}</span> : null}

        <input
          ref={ref}
          className={cx(styles.input, inputClassName)}
          type={type}
          {...props}
          onChange={handleChange}
        />

        {rightIcon ? (
          <span className={styles.rightIcon}>{rightIcon}</span>
        ) : null}
      </div>

      {displayedError ? (
        <div className={styles.errorText}>{displayedError}</div>
      ) : null}
    </div>
  );
});

export default BaseInput;
